import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { postLoginPath } from "../auth/accountMode";
import { ApiError } from "../lib/api";
import { torneos123HeaderLogoSrc } from "../lib/brandLogo";

export default function LoginPage() {
  const { status, user, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recoveryMode = searchParams.get("recuperar");
  const rawNext = searchParams.get("next");
  const nextPath = rawNext && rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : null;
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === "authenticated" && user) {
    return <Navigate to={postLoginPath(user, nextPath)} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const authed = await login(identifier.trim(), password);
      navigate(postLoginPath(authed, nextPath), { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setError("Credenciales inválidas.");
        } else {
          setError(err.message || "No pudimos iniciar sesión.");
        }
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-shell">
      <header className="login-shell__brand">
        <img className="login-shell__logo" src={torneos123HeaderLogoSrc()} alt="Torneos123" />
        <p className="login-shell__eyebrow">Compite · Progresa · Gana</p>
        <p className="login-shell__subtitle">
          Acceso jugadores y administradores
        </p>
      </header>

      <section className="login-shell__card">
        {recoveryMode ? (
          <div className="login-recovery-panel">
            <strong>{recoveryMode === "password" ? "Recuperar contraseña / PIN" : "Recuperar usuario"}</strong>
            <p>
              Si ya existes en Torneos123, no vuelvas a registrarte con otros datos. Usa tu correo o usuario para entrar. Si no recuerdas tu usuario o PIN, solicita al organizador que te lo restablezca desde el panel de jugadores.
            </p>
          </div>
        ) : null}
        <form className="form" onSubmit={handleSubmit}>
          <label>
            Usuario o email
            <input
              type="text"
              placeholder="usuario corto o correo"
              autoComplete="username"
              required
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              disabled={submitting}
            />
          </label>
          <label>
            PIN o contraseña
            <input
              type="password"
              placeholder="PIN o contraseña"
              autoComplete="current-password"
              required
              minLength={1}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={submitting}
            />
          </label>
          {error && (
            <p role="alert" className="form-error">
              {error}
            </p>
          )}
          <button className="btn" type="submit" disabled={submitting}>
            {submitting ? "Entrando…" : "Entrar"}
          </button>
        </form>
        <p className="login-shell__helper">
          Tip: el jugador puede entrar con su usuario corto o correo; la contraseña es el PIN generado por admin.
        </p>
        <p className="login-shell__helper">
          <Link to="/recuperar">¿Olvidaste tu contraseña?</Link>
        </p>
      </section>

      <p className="login-shell__credit">Torneos123 · Sistema de torneos</p>
    </div>
  );
}
